import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { ContactUpdateRequestDTO } from '@/core/api/dtos/request/location/contactUpdate'
import { Location } from '@/core/api/entities/location'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	useUpdateContactLocation,
	usePatchLocation,
	useShareLocationLink,
	useLocation,
} from '@/core/hooks/useLocation'
import { RootState } from '@/core/store'
import { setActiveLocation } from '@/core/store/slices/activeLocationSlice'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { ShareLocationModalSteps } from '../modals/ShareLocationModal'

interface UseLocationShareProps {
	onSuccess?: () => void
	onError?: (error: Error) => void
	locationId?: number
}

export const useLocationShare = (props?: UseLocationShareProps) => {
	const { onSuccess, onError, locationId } = props || {}
	const { showNotification } = useNotificationContext()
	const { t } = useTranslation('common')
	const [selectedLocation, setSelectedLocation] = useState<Location | null>(
		null
	)
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [currentStep, setCurrentStep] = useState<ShareLocationModalSteps>(
		ShareLocationModalSteps.ContactInfo
	)
	const dispatch = useDispatch()

	// State for original and updated contact data
	const [originalContactData, setOriginalContactData] =
		useState<ContactUpdateRequestDTO | null>(null)

	const activeLocation = useSelector(
		(state: RootState) => state.activeLocation.location
	)

	// Get location data using the useLocation hook
	const {
		data: location,
		isLoading,
		error,
		refetch,
	} = useLocation({
		playerId: Number(locationId),
		config: useAccountConfig({
			params: {
				include: 'players',
			},
		}),
		options: {
			enabled: activeLocation === null && !!locationId,
		},
	})

	// Set the active location in the redux store
	useEffect(() => {
		if (location) {
			dispatch(setActiveLocation(location))
		}
	}, [location, dispatch])

	// Update location mutation
	const { mutate: updateContactLocation, isPending: isUpdating } =
		useUpdateContactLocation({
			config: useAccountConfig({}),
			options: {
				onSuccess: () => {
					// Show success notification
					showNotification({
						type: 'success',
						message: t('entities.location.share.update_success.title'),
						description: t(
							'entities.location.share.update_success.description'
						),
					})

					// Move to next step
					setCurrentStep(ShareLocationModalSteps.ShareOptions)

					// Call onSuccess callback if provided
					if (onSuccess) onSuccess()
				},
				onError: (error: Error) => {
					// Show error notification
					showNotification({
						type: 'error',
						message: t('entities.location.share.update_error.title'),
						description: t('entities.location.share.update_error.description'),
					})

					// Call onError callback if provided
					if (onError) onError(error)
				},
			},
		})

	// Regenerate link mutation
	const { mutate: regenerateLink, isPending: isRegenerating } =
		usePatchLocation({
			config: useAccountConfig({}),
			options: {
				onSuccess: async () => {
					// Show success notification
					showNotification({
						type: 'success',
						message: t('entities.location.share.regenerated'),
					})

					// Refetch the location
					if (refetch) refetch()

					// Call onSuccess callback if provided
					if (onSuccess) onSuccess()
				},
				onError: (error: Error) => {
					// Show error notification
					showNotification({
						type: 'error',
						message: t('entities.location.share.regenerate_error.title'),
						description: t(
							'entities.location.share.regenerate_error.description'
						),
					})

					// Call onError callback if provided
					if (onError) onError(error)
				},
			},
		})

	const { mutate: shareLink, isPending: isSharing } = useShareLocationLink({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				// Show success notification
				showNotification({
					type: 'success',
					message: t('entities.location.share.regenerate_success.title'),
					description: t(
						'entities.location.share.regenerate_success.description'
					),
				})

				// Call onSuccess callback if provided
				if (onSuccess) onSuccess()
			},
		},
	})

	const openShareModal = (location: Location) => {
		setSelectedLocation(location)
		// Set location in redux store
		dispatch(setActiveLocation(location))

		// Set original contact data from location
		const contactData = {
			manager: location.manager || '',
			email: location.email || '',
			phone_number: location.phoneNumber || '',
			phone_country_code: location.country || 'US',
		}

		setOriginalContactData(contactData)
		setIsModalVisible(true)
		setCurrentStep(ShareLocationModalSteps.ContactInfo)
	}

	const closeShareModal = () => {
		setIsModalVisible(false)
		setSelectedLocation(null)
		setCurrentStep(ShareLocationModalSteps.ContactInfo)
		setOriginalContactData(null)
	}

	const nextStep = (data: ContactUpdateRequestDTO) => {
		// Build the exact payload first, then compare against original contact data.
		// This avoids missing fields in change detection (e.g. phone_country_code).
		const original = originalContactData
		const nextContactData: ContactUpdateRequestDTO = {
			manager: data.manager,
			email: data.email,
			phone_number: data.phone_number,
			phone_country_code:
				data.phone_country_code ?? original?.phone_country_code ?? 'US',
		}

		const hasChanged =
			original == null ||
			original.manager !== nextContactData.manager ||
			original.email !== nextContactData.email ||
			original.phone_number !== nextContactData.phone_number ||
			original.phone_country_code !== nextContactData.phone_country_code

		if (hasChanged && selectedLocation?.id && data) {
			// Update contact information before proceeding
			updateContactLocation({
				locationId: selectedLocation.id,
				data: nextContactData,
			})
		} else {
			// No changes, proceed to next step without PATCH
			setCurrentStep(ShareLocationModalSteps.ShareOptions)
		}
	}

	const previousStep = () => {
		setCurrentStep(ShareLocationModalSteps.ContactInfo)
	}

	const handleRegenerateLink = () => {
		if (selectedLocation?.id) {
			regenerateLink({
				locationId: selectedLocation.id,
				data: { regenerate: true },
			})
		}
	}

	const handleCopyLink = (shareUrl: string) => {
		navigator.clipboard.writeText(shareUrl)
		showNotification({
			type: 'success',
			message: t('entities.location.share.steps.share_options.copied'),
		})
	}

	const handleShareLink = (shareUrl: string) => {
		if (selectedLocation?.id) {
			shareLink({
				locationId: selectedLocation.id,
				data: { share_url: shareUrl },
			})
		}
	}

	return {
		selectedLocation,
		isModalVisible,
		currentStep,
		originalContactData,
		isUpdating,
		isRegenerating,
		isSharing,
		openShareModal,
		closeShareModal,
		nextStep,
		previousStep,
		handleRegenerateLink,
		handleCopyLink,
		handleShareLink,
		isLoading,
		error,
		refetch,
	}
}

export default useLocationShare
